/**
 * Scout-Onboarding-Wizard (TQ #5325 / Phase 2 von #5135c, Scout-Identity-Fix TQ #5466 Followup 2026-05-21).
 *
 * Erscheint beim First-Login eines Tenant-Owners (= organisation.needs_onboarding=true).
 * Aria fuehrt im Mode `onboarding` durch sechs Slots:
 *   1. Kontext      — wer ist der Tenant, was machen sie, wo
 *   2. Branchenbegriffe — Domain-Vokabular (Bauherr/Position/Aufmass, Bagger/Erdbau, ...)
 *   3. Tonalitaet   — Sie/Du, regionale Direktheit
 *   4. Tools        — gebuchte Services + konkrete Use-Cases
 *   5. Personen     — wer mit Rolle und Kontakt
 *   6. Grenzen      — was Aria autonom darf vs. eskaliert
 *
 * Am Ende generiert Aria das finale 6-Sektion-Markdown, der Wizard
 * persistiert via POST /api/org/<slug>/settings/soul-addendum.
 */

import { useState, useRef, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '../services/api'
import MarkdownMessage from '../components/MarkdownMessage'
import PrivacyModeSelector from '../components/PrivacyModeSelector'

interface Organization {
  id: number
  public_token?: string
  name: string
  slug: string
  member_role: string
}

interface OnboardingWizardProps {
  organization: Organization
  onComplete: () => void
}

type ChatRole = 'user' | 'assistant'

interface ChatMessage {
  role: ChatRole
  content: string
  ts: number
}

const SUMMARY_PROMPT = `Bitte fasse jetzt unsere bisherigen Gespräche im exakten Format unten zusammen.
Schreibe NUR das Markdown, keine Einleitung, keine Schlussbemerkung.

Format (alle 6 Sektionen, auch wenn knapp):

## Kontext
(2-4 Sätze: Wer ist die Organisation, was machen sie, wo, wie groß.)

## Branchenbegriffe
(Domain-Vokabular: 5-10 Bullet-Points mit Begriff und kurzer Bedeutung.)

## Tonalität
(Sie/Du, regional/förmlich, Direktheit, Humor — 2-3 Sätze.)

## Tools
(Welche Services nutzen sie, wofür konkret — pro Service ein Bullet.)

## Personen
(Schlüsselpersonen: Name, Rolle, Kontakt — pro Person ein Bullet.)

## Grenzen
(Was Aria autonom darf vs. was eskaliert wird — 3-5 Bullet-Points.)`

// TQ #6264: Reality-Check gegen ERPNext-Subs, damit Scout nicht Module
// halluziniert (Glasbrenner-Vorfall 2026-05-24: Scout sagte 'MAIL, WEB,
// STORAGE, ERP', tatsaechlich war 0 Active Sub gebucht).
// Liefert einen Klartext-Satz fuer die Initial-Begruessung.
function buildServicesContext(services: Array<{ item_name?: string; plan_name?: string; status?: string }>): string {
  if (!Array.isArray(services) || services.length === 0) {
    return 'Aktuell sind noch keine Services gebucht.'
  }
  const names = services
    .map(s => s.item_name || s.plan_name || '')
    .filter(Boolean)
  if (names.length === 0) {
    return 'Es liegen aktive Subscriptions vor, aber ohne benannte Plaene.'
  }
  // Doppelte deduplizieren (Multi-Plan-Subs liefern manchmal denselben item_name mehrfach)
  const unique = Array.from(new Set(names))
  return `Aktuell sind folgende Services aktiv: ${unique.join(', ')}.`
}

export default function OnboardingWizard({ organization, onComplete }: OnboardingWizardProps) {
  const navigate = useNavigate()
  // TQ #8459/#10086: Datenschutz-/Leistungs-Auswahl fehlte komplett im
  // Onboarding-Ablauf. Erster Schritt VOR dem Scout-Chat, damit die Wahl
  // getroffen ist bevor ueberhaupt Unternehmensdaten mit Aria besprochen
  // werden. Backend-Default ist eu_only (fail-safe) -- "Weiter" ohne
  // Aenderung ist also sicher, kein Zwang zu einer expliziten Wahl.
  const [step, setStep] = useState<'privacy' | 'chat'>('privacy')
  const isAdminLike = organization.member_role === 'owner' || organization.member_role === 'admin'
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [sessionId, setSessionId] = useState<string | undefined>(undefined)
  const [input, setInput] = useState('')
  const [busy, setBusy] = useState(false)
  const [finishing, setFinishing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const initialSent = useRef(false)

  const base = `/${organization.public_token || organization.slug}`

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }
  useEffect(() => { scrollToBottom() }, [messages, finishing])

  useEffect(() => {
    const ta = textareaRef.current
    if (!ta) return
    ta.style.height = 'auto'
    ta.style.height = `${Math.min(ta.scrollHeight, 200)}px`
  }, [input])

  const sendMessage = useCallback(async (text: string, isInitial = false) => {
    setError(null)
    setBusy(true)
    if (!isInitial) {
      setMessages(m => [...m, { role: 'user', content: text, ts: Date.now() }])
    }
    try {
      const resp = await api.cortexChat(text, sessionId, 'onboarding')
      if (resp.session_id && !sessionId) setSessionId(resp.session_id)
      setMessages(m => [...m, { role: 'assistant', content: resp.response, ts: Date.now() }])
    } catch (e: any) {
      const apiErr = e?.error || e?.message || 'Scout ist gerade nicht erreichbar.'
      setError(apiErr)
    } finally {
      setBusy(false)
    }
  }, [sessionId])

  // Begrüssung beim Erst-Mount: Scout fragt selbst die erste Slot ab,
  // ohne dass der User selbst tippen muss. "Hallo" reicht als Trigger,
  // Scout's Onboarding-Soul macht den Rest.
  // TQ #6264: Vor der Begruessung holen wir die aktuellen Subscriptions
  // aus ERPNext und injizieren sie ALS FAKT in die Initial-Message —
  // verhindert dass Scout sich Module ausdenkt.
  // TQ #6471 (#6462): Bei Re-Onboarding (existing soul_addendum vorhanden)
  // den bisherigen Persona-Stand mit-injizieren, damit Scout daran anknuepft
  // statt von Null zu starten. Save bleibt full-replace (Scout liefert am Ende
  // den neuen kompletten Markdown).
  useEffect(() => {
    if (step !== 'chat') return
    if (initialSent.current) return
    initialSent.current = true
    const slug = organization.public_token || organization.slug
    void (async () => {
      let servicesContext = 'Aktuell sind noch keine Services gebucht.'
      try {
        const resp = await api.getOrgServices(slug)
        servicesContext = buildServicesContext(resp?.services || [])
      } catch {
        // Wenn portal-api nicht erreichbar: fail-safe als 'keine Services bekannt' —
        // besser als zu halluzinieren.
        servicesContext = 'Subscriptions konnten nicht geladen werden — bitte frage offen nach.'
      }

      // Existing soul_addendum laden (fail-safe — bei Fehler einfach Neu-Onboarding-Flow)
      let existingSoul: { soul_addendum: string; filled: boolean } | null = null
      try {
        existingSoul = await api.getOrgSoulAddendum(slug)
      } catch {
        // ignore — wir fallen auf Neu-Onboarding zurueck
      }

      if (existingSoul?.filled && existingSoul.soul_addendum.trim().length >= 20) {
        sendMessage(
          `Hallo Scout, wir sind ${organization.name} und wollen ein Re-Onboarding machen — also unser Persona-Profil aktualisieren, nicht von Null neu erstellen.\n\nHier ist unser bisheriger Stand (6-Sektionen-Markdown):\n\n${existingSoul.soul_addendum}\n\nAktueller Service-Stand laut ERPNext:\n${servicesContext}\n\nBitte frag uns konkret, was sich seit dem letzten Onboarding geaendert hat (Personen / Tools / Tonalitaet / Branchen-Schwerpunkte / Grenzen). Wir bestaetigen oder korrigieren Punkt fuer Punkt. Am Ende lieferst du das vollstaendige aktualisierte 6-Sektionen-Markdown.`,
          true,
        )
      } else {
        sendMessage(
          `Hallo Scout, wir sind neu hier. Wir sind ${organization.name}. ${servicesContext} Bitte starte das Onboarding.`,
          true,
        )
      }
    })()
  }, [step, organization.name, organization.public_token, organization.slug, sendMessage])

  const sendFromInput = () => {
    const text = input.trim()
    if (!text || busy || finishing) return
    setInput('')
    sendMessage(text)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    sendFromInput()
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      sendFromInput()
    }
  }

  const finish = async () => {
    if (busy || finishing) return
    setError(null)
    setFinishing(true)
    setMessages(m => [...m, {
      role: 'user',
      content: '✓ Wizard abschließen — bitte erstelle jetzt das Persona-Profil.',
      ts: Date.now(),
    }])
    try {
      // 1. Sende den Summary-Prompt, Scout liefert das 6-Sektion-Markdown.
      const summary = await api.cortexChat(SUMMARY_PROMPT, sessionId, 'onboarding')
      const soulAddendum = summary.response.trim()
      if (soulAddendum.length < 50) {
        throw new Error('Scout hat kein vollständiges Persona-Profil geliefert — bitte führe das Gespräch noch etwas weiter.')
      }
      setMessages(m => [...m, { role: 'assistant', content: soulAddendum, ts: Date.now() }])

      // 2. Persistiere in cortex-db.org_settings.soul_addendum via portal-api.
      await api.putOrgSoulAddendum(organization.public_token || organization.slug, soulAddendum)

      // 3. Refresh org from outside (needs_onboarding wird false), dann nav.
      onComplete()
      setTimeout(() => navigate(base, { replace: true }), 800)
    } catch (e: any) {
      const apiErr = e?.error || e?.message || 'Speichern fehlgeschlagen.'
      setError(apiErr)
      setFinishing(false)
    }
  }

  const hasEnoughHistory = messages.filter(m => m.role === 'user').length >= 3

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(180deg, #1a1a2e 0%, #16213e 100%)',
      padding: '24px',
      display: 'flex',
      justifyContent: 'center',
    }}>
      <div style={{
        maxWidth: '800px',
        width: '100%',
        background: 'white',
        borderRadius: '16px',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        maxHeight: '90vh',
      }}>
        {step === 'privacy' && (
          <>
            <div style={{
              padding: '20px 24px',
              background: 'linear-gradient(135deg, #FF6B35 0%, #F7931E 100%)',
              color: 'white',
              borderBottom: '1px solid rgba(0,0,0,0.06)',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '1.5px', opacity: 0.9 }}>
                    Willkommen bei IT-SCE
                  </div>
                  <h1 style={{ margin: '4px 0 0', fontSize: '24px' }}>
                    Datenschutz &amp; Leistung für {organization.name}
                  </h1>
                </div>
                <div style={{
                  padding: '6px 12px',
                  background: 'rgba(255,255,255,0.15)',
                  borderRadius: '20px',
                  fontSize: '12px',
                  fontWeight: 600,
                }}>
                  Onboarding
                </div>
              </div>
              <p style={{ margin: '8px 0 0', fontSize: '14px', opacity: 0.95, lineHeight: '1.5' }}>
                Bevor Scout mit euch spricht: legt fest, wo Aria eure Anfragen verarbeiten darf.
                Ihr könnt das jederzeit später unter Einstellungen → Datenschutz ändern.
              </p>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px', background: '#f8fafc' }}>
              <PrivacyModeSelector canEdit={isAdminLike} />
            </div>
            <div style={{
              padding: '16px 24px',
              borderTop: '1px solid #e5e7eb',
              background: 'white',
              display: 'flex',
              justifyContent: 'flex-end',
            }}>
              <button
                type="button"
                onClick={() => setStep('chat')}
                style={{
                  padding: '10px 18px',
                  background: '#FF6B35',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Weiter
              </button>
            </div>
          </>
        )}
        {step === 'chat' && (
        <>
        <div style={{
          padding: '20px 24px',
          background: 'linear-gradient(135deg, #FF6B35 0%, #F7931E 100%)',
          color: 'white',
          borderBottom: '1px solid rgba(0,0,0,0.06)',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '1.5px', opacity: 0.9 }}>
                Willkommen bei IT-SCE
              </div>
              <h1 style={{ margin: '4px 0 0', fontSize: '24px' }}>
                Scout lernt {organization.name} kennen — für Aria
              </h1>
            </div>
            <div style={{
              padding: '6px 12px',
              background: 'rgba(255,255,255,0.15)',
              borderRadius: '20px',
              fontSize: '12px',
              fontWeight: 600,
            }}>
              Onboarding
            </div>
          </div>
          <p style={{ margin: '8px 0 0', fontSize: '14px', opacity: 0.95, lineHeight: '1.5' }}>
            Scout fragt dich ein paar Dinge zu deinem Unternehmen ab, damit Aria
            dich später möglichst persönlich unterstützen kann. Antworte einfach
            so, wie du es einem neuen Kollegen erklären würdest.
          </p>
        </div>

        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '20px 24px',
          background: '#f8fafc',
        }}>
          {messages.map((m, i) => (
            <div
              key={`${m.ts}-${i}`}
              style={{
                display: 'flex',
                justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start',
                marginBottom: '12px',
              }}
            >
              <div style={{
                maxWidth: '78%',
                padding: '10px 14px',
                background: m.role === 'user' ? '#FF6B35' : 'white',
                color: m.role === 'user' ? 'white' : '#1f2937',
                borderRadius: '14px',
                borderTopRightRadius: m.role === 'user' ? '4px' : '14px',
                borderTopLeftRadius: m.role === 'assistant' ? '4px' : '14px',
                boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
                fontSize: '14px',
                lineHeight: '1.5',
                whiteSpace: m.role === 'user' ? 'pre-wrap' : 'normal',
                wordBreak: 'break-word',
              }}>
                {m.role === 'assistant' ? <MarkdownMessage content={m.content} /> : m.content}
              </div>
            </div>
          ))}
          {busy && (
            <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: '12px' }}>
              <div style={{
                padding: '10px 14px',
                background: 'white',
                color: '#9ca3af',
                borderRadius: '14px',
                borderTopLeftRadius: '4px',
                boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
                fontSize: '14px',
                fontStyle: 'italic',
              }}>
                Scout überlegt …
              </div>
            </div>
          )}
          {error && (
            <div style={{
              marginTop: '12px',
              padding: '10px 14px',
              background: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '8px',
              color: '#991b1b',
              fontSize: '13px',
            }}>
              {error}
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        <form onSubmit={handleSubmit} style={{
          padding: '16px 24px',
          borderTop: '1px solid #e5e7eb',
          background: 'white',
        }}>
          <div style={{ display: 'flex', gap: '8px' }}>
            <textarea
              ref={textareaRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={finishing ? 'Wizard wird abgeschlossen…' : 'Antworten oder Frage stellen… (Enter sendet, Shift+Enter neue Zeile)'}
              disabled={busy || finishing}
              rows={1}
              style={{
                flex: 1,
                padding: '10px 14px',
                border: '1px solid #d1d5db',
                borderRadius: '10px',
                fontSize: '14px',
                outline: 'none',
                resize: 'none',
                minHeight: '40px',
                maxHeight: '200px',
                fontFamily: 'inherit',
                lineHeight: '1.5',
                overflowY: 'auto',
              }}
            />
            <button
              type="submit"
              disabled={busy || finishing || !input.trim()}
              style={{
                padding: '10px 18px',
                background: (busy || finishing || !input.trim()) ? '#cbd5e1' : '#FF6B35',
                color: 'white',
                border: 'none',
                borderRadius: '10px',
                fontWeight: 600,
                cursor: (busy || finishing || !input.trim()) ? 'not-allowed' : 'pointer',
              }}
            >
              Senden
            </button>
          </div>
          <div style={{
            marginTop: '12px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '12px',
            fontSize: '12px',
            color: '#6b7280',
          }}>
            <div>
              {hasEnoughHistory
                ? '✓ Genug Kontext gesammelt — du kannst den Wizard jetzt abschließen.'
                : 'Antworte auf mindestens 3 Fragen, bevor du abschließt.'}
            </div>
            <button
              type="button"
              onClick={finish}
              disabled={!hasEnoughHistory || busy || finishing}
              style={{
                padding: '8px 14px',
                background: (!hasEnoughHistory || busy || finishing) ? '#e5e7eb' : '#10b981',
                color: (!hasEnoughHistory || busy || finishing) ? '#9ca3af' : 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: 600,
                cursor: (!hasEnoughHistory || busy || finishing) ? 'not-allowed' : 'pointer',
              }}
            >
              {finishing ? 'Speichere…' : 'Wizard abschließen'}
            </button>
          </div>
        </form>
        </>
        )}
      </div>
    </div>
  )
}
